import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, Navigate } from 'react-router-dom';
import {
  LayoutDashboard, Rocket, Wallet, ArrowLeftRight,
  Key, Webhook, CheckCircle2, Settings, LogOut, Menu, Palette, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import { PlatformProvider, usePlatform } from '../context/PlatformContext';

const navItems = [
  { to: '/platform/dashboard', label: 'Overview', icon: LayoutDashboard },
  { to: '/platform/get-started', label: 'Get Started', icon: Rocket },
  { to: '/platform/wallet-activation', label: 'Wallet Activation', icon: CreditCard },
  { to: '/platform/wallets', label: 'Wallets', icon: Wallet },
  { to: '/platform/transactions', label: 'Transactions', icon: ArrowLeftRight },
  { to: '/platform/api-keys', label: 'API Keys', icon: Key },
  { to: '/platform/webhooks', label: 'Webhooks', icon: Webhook },
  { to: '/platform/customize', label: 'Customize', icon: Palette },
  { to: '/platform/go-live', label: 'Go Live', icon: CheckCircle2 },
  { to: '/platform/settings', label: 'Settings', icon: Settings },
];

const statusColors = {
  sandbox: 'bg-yellow-100 text-yellow-800',
  pending_review: 'bg-blue-100 text-blue-800',
  live: 'bg-green-100 text-green-800',
};

const statusLabels = {
  sandbox: 'Sandbox',
  pending_review: 'Under Review',
  live: 'Live',
};

function LayoutInner() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { tenant } = usePlatform();

  const handleLogout = () => {
    localStorage.removeItem('platform_token');
    localStorage.removeItem('platform_tenant');
    localStorage.removeItem('platform_first_api_key');
    navigate('/platform/login');
    toast.success('Logged out');
  };

  return (
    <div className="flex h-screen bg-gray-50" data-testid="platform-layout">
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 
        transform transition-transform duration-200 ease-in-out
        lg:relative lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#E8678B] flex items-center justify-center">
              <span className="text-white font-bold text-sm">P</span>
            </div>
            <span className="font-semibold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Pebbles
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 truncate" data-testid="tenant-name">
              {tenant.name || 'My Business'}
            </span>
            <span
              data-testid="tenant-status-badge"
              className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[tenant.status] || statusColors.sandbox}`}
            >
              {statusLabels[tenant.status] || 'Sandbox'}
            </span>
          </div>
        </div>

        <nav className="p-3 space-y-1 overflow-y-auto" data-testid="platform-sidebar-nav" style={{ maxHeight: 'calc(100vh - 160px)' }}>
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) => `
                flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                ${isActive
                  ? 'bg-pink-50 text-[#E8678B]'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }
              `}
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-100 bg-white">
          <button
            onClick={handleLogout}
            data-testid="platform-logout-btn"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 w-full transition-colors"
          >
            <LogOut size={18} />
            Log out
          </button>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="lg:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
          <button onClick={() => setSidebarOpen(true)} className="text-gray-600" data-testid="mobile-menu-btn">
            <Menu size={24} />
          </button>
          <span className="font-semibold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Pebbles
          </span>
          <div className="w-6" />
        </header>

        <main className="flex-1 overflow-y-auto p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default function PlatformLayout() {
  const token = localStorage.getItem('platform_token');
  if (!token) {
    return <Navigate to="/platform/login" replace />;
  }

  return (
    <PlatformProvider>
      <LayoutInner />
    </PlatformProvider>
  );
}
